import { useTheme } from "@/components/ThemeProvider";
import { LanguageSelector } from "@/components/LanguageSelector";
import { useLanguage } from "@/context/LanguageContext";
import { useState, useEffect } from "react";
import { Sun, Moon, Menu, X } from "lucide-react";
import { Button } from "@/components/ui/button";

export const Navbar = () => {
  const { theme, setTheme } = useTheme();
  const { t } = useLanguage();
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Add scroll event listener
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const toggleTheme = () => {
    setTheme(theme === "dark" ? "light" : "dark");
  };

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
      setMobileMenuOpen(false);
    }
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled
          ? "bg-background/80 backdrop-blur-md shadow-md"
          : "bg-transparent"
      }`}
    >
      <nav className="container mx-auto px-6 py-4 flex items-center justify-between">
        {/* Logo area - currently text, but prepared for SVG logo */}
        <a
          href="#"
          onClick={(e) => {
            e.preventDefault();
            scrollToTop();
          }}
          className="text-xl font-bold cursor-pointer flex items-center"
          aria-label="Aditya Kandele logo, go to top of page"
        >
          {/* Uncomment and add SVG logo when available
          <svg className="h-8 w-auto mr-2" viewBox="0 0 24 24">
            [SVG path data goes here]
          </svg>
          */}
          {t("root.title")}
        </a>

        {/* Desktop Navigation */}
        <div className="hidden md:flex items-center space-x-8">
          <a
            href="#home"
            onClick={() => scrollToSection("home")}
            className="nav-link"
          >
            {t("nav.home")}
          </a>
          <a
            href="#experience"
            onClick={() => scrollToSection("experience")}
            className="nav-link"
          >
            {t("nav.experience")}
          </a>
          <a
            href="#projects"
            onClick={() => scrollToSection("projects")}
            className="nav-link"
          >
            {t("nav.projects")}
          </a>
          <a
            href="#skills"
            onClick={() => scrollToSection("skills")}
            className="nav-link"
          >
            {t("nav.skills")}
          </a>
          <a
            href="#contact"
            onClick={() => scrollToSection("contact")}
            className="nav-link"
          >
            {t("nav.contact")}
          </a>
        </div>

        {/* Theme and Language Controls */}
        <div className="hidden md:flex items-center space-x-2">
          <Button
            variant="ghost"
            size="icon"
            aria-label="Toggle theme"
            onClick={toggleTheme}
          >
            {theme === "dark" ? (
              <Sun className="h-5 w-5" />
            ) : (
              <Moon className="h-5 w-5" />
            )}
          </Button>
          <LanguageSelector />
        </div>

        {/* Mobile Menu Button */}
        <div className="md:hidden flex items-center space-x-2">
          <Button
            variant="ghost"
            size="icon"
            aria-label="Toggle theme"
            onClick={toggleTheme}
          >
            {theme === "dark" ? (
              <Sun className="h-5 w-5" />
            ) : (
              <Moon className="h-5 w-5" />
            )}
          </Button>
          <LanguageSelector />
          <Button
            variant="ghost"
            size="icon"
            aria-label="Toggle menu"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          >
            {mobileMenuOpen ? (
              <X className="h-6 w-6" />
            ) : (
              <Menu className="h-6 w-6" />
            )}
          </Button>
        </div>
      </nav>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="md:hidden bg-background/95 backdrop-blur-md shadow-md px-6 py-4 animate-fade-in">
          <div className="flex flex-col space-y-4">
            <a
              href="#home"
              onClick={() => scrollToSection("home")}
              className="nav-link"
            >
              {t("nav.home")}
            </a>
            <a
              href="#experience"
              onClick={() => scrollToSection("experience")}
              className="nav-link"
            >
              {t("nav.experience")}
            </a>
            <a
              href="#projects"
              onClick={() => scrollToSection("projects")}
              className="nav-link"
            >
              {t("nav.projects")}
            </a>
            <a
              href="#skills"
              onClick={() => scrollToSection("skills")}
              className="nav-link"
            >
              {t("nav.skills")}
            </a>
            <a
              href="#contact"
              onClick={() => scrollToSection("contact")}
              className="nav-link"
            >
              {t("nav.contact")}
            </a>
          </div>
        </div>
      )}
    </header>
  );
};
